import React, { useState, useEffect, useRef, useMemo } from 'react';
import StatCard from '../components/stat-card';
import SearchBar from '../components/search-bar';
import MarketTable from '../components/market-table';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import OnboardingTour from '../components/onboarding-tour';
import { DASHBOARD_TOUR_STEPS } from '../config/onboarding-steps';
import { useAuth } from '../context/auth-context';
import { fetchTopCoins, fetchStablecoinIds } from '../services/coingecko-api';
import { fetchAllBinanceTickers, fetchBinanceKlinesBatch } from '../services/binance-api';
import { isStablecoin, buildSymbolIndex } from '../utils/symbol-mapper';
import { getMarketStatistics, getTopMovers } from '../services/insights-service';
import {
  calculateRSI,
  calculateMACD,
  calculateStochastic
} from '../utils/technical-indicators';
import { calculateTrend } from '../utils/trend-calculator';
import { computeSMA50 } from '../indicators/sma-50';
import { parseBinanceKlines, extractPrices } from '../utils/timeframe-utils';
import { useInterval } from '../hooks/use-interval';
import { fetchServerMarketData, fetchStocksMarketData, fetchTaoSubnetsData, isServerApiAvailable } from '../services/server-api';
import './dashboard-page.css';

function DashboardPage() {
  const { showOnboarding, dismissOnboarding } = useAuth();
  const [marketData, setMarketData]           = useState([]);
  const [searchTerm, setSearchTerm]           = useState('');
  const [isLoading, setIsLoading]             = useState(true);
  const [isLoadingIndicators, setIsLoadingIndicators] = useState(false);
  const [error, setError]                     = useState(null);
  const [lastUpdated, setLastUpdated]         = useState(null);
  const [serverInitialising, setServerInitialising] = useState(false);
  const [assetMode, setAssetMode]             = useState('crypto');
  const [directionFilter, setDirectionFilter]  = useState('all');
  const [showUntracked, setShowUntracked]      = useState(false);

  // Prevent double-fetch in React StrictMode (dev only)
  const fetchingRef = useRef(false);

  // ─── TA calculator ────────────────────────────────────────────────────────────
  function calcIndicators(priceData) {
    if (!priceData || !priceData.closes || priceData.closes.length < 30) {
      return { rsi: null, macd: null, stochastic: null, trend: 'N/A', sma50Slope: null };
    }
    const rsi        = calculateRSI(priceData.closes, 7);
    const macd       = calculateMACD(priceData.closes, 12, 26, 9);
    const stochastic = calculateStochastic(priceData.highs, priceData.lows, priceData.closes, 14, 3);
    const sma50      = computeSMA50(priceData.closes);
    return {
      rsi, macd, stochastic,
      trend: calculateTrend(
        rsi[rsi.length - 1],
        macd.histogram[macd.histogram.length - 1],
        stochastic.k[stochastic.k.length - 1],
        stochastic.d[stochastic.d.length - 1]
      ),
      sma50Slope: sma50.slope,
    };
  }

  // ─── Main fetch ───────────────────────────────────────────────────────────────
  const fetchMarketData = async (silent = false, mode = assetMode) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      // ── Stocks mode: VPS-only path ────────────────────────────────────────
      if (mode === 'stocks') {
        if (!isServerApiAvailable()) {
          setError('Stocks data requires the VPS server. Start the server and refresh.');
          setIsLoading(false);
          fetchingRef.current = false;
          return;
        }
        try {
          console.log('[Dashboard] Stocks mode — fetching pre-computed stocks data...');
          const { stocks: serverStocks, lastUpdated: serverUpdated } = await fetchStocksMarketData();

          const enriched = serverStocks.map(stock => {
            const ind = stock.indicators || {};
            return {
              ...stock,
              trends: {
                weekly:   ind.weekly?.trend   || 'N/A',
                daily:    ind.daily?.trend     || 'N/A',
                fourHour: ind.fourHour?.trend  || 'N/A',
              },
              indicators: ind,
              historicalData: null,
              binanceSymbol: null,
            };
          });

          setMarketData(enriched);
          setLastUpdated(new Date(serverUpdated).getTime());
          setIsLoading(false);
          setIsLoadingIndicators(false);
          setServerInitialising(false);
          console.log(`[Dashboard] Stocks: loaded ${enriched.length} stocks with pre-computed indicators.`);
          return;
        } catch (stocksErr) {
          console.error('[Dashboard] Stocks fetch failed:', stocksErr.message);
          const isNetErr = stocksErr.message === 'Failed to fetch'
            || stocksErr.message?.includes('NetworkError')
            || stocksErr.message?.includes('ERR_CONNECTION_REFUSED')
            || stocksErr.message?.includes('Load failed');
          const msg = isNetErr
            ? 'Cannot reach the server at localhost:3001. Run "npm run server" to start it.'
            : stocksErr.message === 'Stocks data not yet available'
              ? 'Stocks data is still loading — the server needs ~5 min to fetch all 100 stocks. Refresh in a moment.'
              : `Failed to load stocks data: ${stocksErr.message}`;
          setError(msg);
          setIsLoading(false);
          fetchingRef.current = false;
          return;
        }
      }

      // ── Tao Subnets mode: VPS-only, Phase 1 — market data only, no indicators ──
      if (mode === 'tao-subnets') {
        if (!isServerApiAvailable()) {
          setError('Tao Subnets data requires the VPS server. Start the server and refresh.');
          setIsLoading(false);
          fetchingRef.current = false;
          return;
        }
        try {
          console.log('[Dashboard] Tao Subnets mode — fetching pre-computed subnet data...');
          const { subnets, lastUpdated: serverUpdated } = await fetchTaoSubnetsData();

          const enriched = subnets.map(subnet => ({
            ...subnet,
            trends: { weekly: 'N/A', daily: 'N/A', fourHour: 'N/A' },
            indicators: null,
            historicalData: null,
            binanceSymbol: null,
          }));

          setMarketData(enriched);
          setLastUpdated(new Date(serverUpdated).getTime());
          setIsLoading(false);
          setIsLoadingIndicators(false);
          setServerInitialising(false);
          console.log(`[Dashboard] Tao Subnets: loaded ${enriched.length} subnets.`);
          return;
        } catch (subnetErr) {
          console.error('[Dashboard] Tao Subnets fetch failed:', subnetErr.message);
          const isNetErr = subnetErr.message === 'Failed to fetch'
            || subnetErr.message?.includes('NetworkError')
            || subnetErr.message?.includes('ERR_CONNECTION_REFUSED')
            || subnetErr.message?.includes('Load failed');
          const msg = isNetErr
            ? 'Cannot reach the server at localhost:3001. Run "npm run server" to start it.'
            : subnetErr.message?.includes('not yet available')
              ? 'Tao Subnets data is still loading, or TAOSTATS_API_KEY is not configured on the server.'
              : `Failed to load Tao Subnets data: ${subnetErr.message}`;
          setError(msg);
          setIsLoading(false);
          fetchingRef.current = false;
          return;
        }
      }

      // Fetch CoinGecko stablecoin category up front so both VPS and direct paths can use it
      const cgStablecoinsEarly = await fetchStablecoinIds();
      const isStableEarly = (id) => cgStablecoinsEarly ? cgStablecoinsEarly.has(id) : isStablecoin(id);

      // ── VPS fast-path: pre-computed data from the Express server ─────────
      if (isServerApiAvailable()) {
        try {
          console.log('[Dashboard] VPS mode — fetching pre-computed market data...');
          const { coins: serverCoins, lastUpdated: serverUpdated } = await fetchServerMarketData();

          const enriched = serverCoins
            .filter(coin => !isStableEarly(coin.id))
            .map(coin => {
              const ind = coin.indicators || {};
              return {
                ...coin,
                trends: {
                  weekly:   ind.weekly?.trend   || 'N/A',
                  daily:    ind.daily?.trend     || 'N/A',
                  fourHour: ind.fourHour?.trend  || 'N/A',
                },
                indicators: ind,
                historicalData: null,
                binanceSymbol: null,
              };
            });

          setMarketData(enriched);
          setLastUpdated(new Date(serverUpdated).getTime());
          setIsLoading(false);
          setIsLoadingIndicators(false);
          setServerInitialising(false);
          console.log(`[Dashboard] VPS: loaded ${enriched.length} coins with pre-computed indicators.`);
          return;
        } catch (vpsErr) {
          // 503 = server is still doing its first data refresh — don't hammer CoinGecko,
          // just show a "starting up" message and let the retry interval try again.
          if (vpsErr.message === 'Data not yet available') {
            console.log('[Dashboard] Server is initialising — will retry in 10s');
            setServerInitialising(true);
            setIsLoading(false);
            fetchingRef.current = false;
            return;
          }
          // Network error = server is down entirely. CoinGecko is also proxied through
          // the server in VPS mode, so falling through would also fail. Show a clear message.
          const isNetworkError = vpsErr.message === 'Failed to fetch'
            || vpsErr.message?.includes('NetworkError')
            || vpsErr.message?.includes('ERR_CONNECTION_REFUSED')
            || vpsErr.message?.includes('Load failed');
          if (isNetworkError) {
            setError('Cannot reach the server at localhost:3001. Run "npm run server" to start it.');
            setIsLoading(false);
            fetchingRef.current = false;
            return;
          }
          // Any other VPS error — fall through to direct CoinGecko fetch
          setServerInitialising(false);
          console.warn('[Dashboard] VPS pre-computed fetch failed, falling back:', vpsErr.message);
        }
      }

      // ── Stage 1: prices (fast — show table immediately) ──────────────────
      console.log('[Dashboard] Fetching CoinGecko metadata...');
      const rawCoins = await fetchTopCoins(80);
      if (!rawCoins || rawCoins.length === 0) throw new Error('No market data received');

      // isStableEarly uses CoinGecko's stablecoin category (fetched above); fall back to hardcoded list
      const isStable = isStableEarly;

      // Only show tradeable coin pairs — exclude stablecoins
      const coins = rawCoins.filter(coin => !isStable(coin.id));

      const symbolIndex    = buildSymbolIndex(coins);
      const binanceSymbols = symbolIndex.map(e => e.binanceSymbol);
      console.log(`[Dashboard] ${symbolIndex.length}/${coins.length} tradeable coins mapped to Binance`);

      const symbolLookup = {};
      symbolIndex.forEach(({ coinGeckoId, binanceSymbol }) => {
        symbolLookup[coinGeckoId] = binanceSymbol;
      });

      const tickerMap = await fetchAllBinanceTickers(binanceSymbols);
      console.log('[Dashboard] Tickers received. Showing table...');

      // Build initial coin list with N/A trends — safe for TrendBadge
      const initialCoins = coins.map(coin => {
        const binanceSym = symbolLookup[coin.id];
        const ticker     = binanceSym ? tickerMap[binanceSym] : null;
        return {
          ...coin,
          current_price:               ticker ? parseFloat(ticker.lastPrice)          : coin.current_price,
          price_change_percentage_24h: ticker ? parseFloat(ticker.priceChangePercent) : coin.price_change_percentage_24h,
          total_volume:                coin.total_volume,
          trends:      { weekly: 'N/A', daily: 'N/A', fourHour: 'N/A' },
          indicators:  null,
          historicalData: null,
          binanceSymbol: binanceSym || null,
        };
      });

      setMarketData(initialCoins);
      setLastUpdated(Date.now());
      if (!silent) setIsLoading(false);

      console.log('[Dashboard] Table rendered. Fetching klines for indicators...');

      // ── Stage 2: klines + TA (slower — fills in trend badges) ────────────
      setIsLoadingIndicators(true);
      const [fourHourRaw, dailyRaw, weeklyRaw] = await Promise.all([
        fetchBinanceKlinesBatch(symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval: '4h',  limit: 200 }))),
        fetchBinanceKlinesBatch(symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval: '1d',  limit: 90  }))),
        fetchBinanceKlinesBatch(symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval: '1w',  limit: 52  }))),
      ]);
      console.log('[Dashboard] Klines received. Computing indicators...');

      const coinsWithTrends = initialCoins.map(coin => {
        const sym = coin.binanceSymbol;
        if (!sym || isStable(coin.id)) {
          return isStable(coin.id)
            ? { ...coin, trends: { weekly: 'Stable', daily: 'Stable', fourHour: 'Stable' } }
            : coin;
        }
        try {
          const fh = parseBinanceKlines(fourHourRaw[sym]);
          const d  = parseBinanceKlines(dailyRaw[sym]);
          const w  = parseBinanceKlines(weeklyRaw[sym]);

          const fhInd = calcIndicators(extractPrices(fh));
          const dInd  = calcIndicators(extractPrices(d));
          const wInd  = calcIndicators(extractPrices(w));

          return {
            ...coin,
            trends:     { weekly: wInd.trend, daily: dInd.trend, fourHour: fhInd.trend },
            indicators: { weekly: wInd, daily: dInd, fourHour: fhInd },
            historicalData: {
              prices:   fh.slice(-60).map(c => [c[0], c[4]]),
              fourHour: fh, daily: d, weekly: w,
            },
          };
        } catch (err) {
          console.warn(`[Dashboard] Indicator error for ${coin.id}:`, err);
          return coin;
        }
      });

      setMarketData(coinsWithTrends);
      setLastUpdated(Date.now());

      const count = coinsWithTrends.filter(c => c.trends?.daily !== 'N/A').length;
      console.log(`[Dashboard] Done — ${count}/${coins.length} coins have trend analysis.`);

    } catch (err) {
      console.error('[Dashboard] Error:', err);
      setError(`Failed to load market data: ${err.message}`);
      if (!silent) setIsLoading(false);
    } finally {
      setIsLoadingIndicators(false);
      fetchingRef.current = false;
    }
  };

  // ─── Asset mode toggle ────────────────────────────────────────────────────────
  const handleModeChange = (newMode) => {
    if (newMode === assetMode) return;
    setAssetMode(newMode);
    setMarketData([]);
    setError(null);
    fetchingRef.current = false;
    fetchMarketData(false, newMode);
  };

  // ─── Lifecycle ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchMarketData(false, assetMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normal refresh every 2 minutes once loaded
  useInterval(() => fetchMarketData(true, assetMode), 2 * 60 * 1000);

  // Fast retry every 10 seconds while server is still initialising
  useInterval(() => {
    if (serverInitialising) fetchMarketData(false, assetMode);
  }, 10 * 1000);

  // ─── Derived stats ────────────────────────────────────────────────────────────
  const marketStats = useMemo(() => getMarketStatistics(marketData), [marketData]);
  const topMovers   = useMemo(() => getTopMovers(marketData, 1),     [marketData]);

  const trackedData = useMemo(() => {
    // "Untracked" (no exchange indicator data) is a crypto-only concept —
    // Tao Subnets (Phase 1) has no indicators at all yet, and stocks always
    // compute real indicators, so this filter only makes sense in crypto mode.
    if (assetMode !== 'crypto' || showUntracked || isLoadingIndicators) return marketData;
    return marketData.filter(coin =>
      !(coin.trends?.weekly === 'N/A' &&
        coin.trends?.daily === 'N/A' &&
        coin.trends?.fourHour === 'N/A')
    );
  }, [marketData, assetMode, showUntracked, isLoadingIndicators]);

  const directionFilteredData = useMemo(() => {
    if (directionFilter === 'all') return trackedData;
    const dir = directionFilter === 'long' ? 'Long' : 'Short';
    return trackedData.filter(coin =>
      coin.trends?.weekly === dir &&
      coin.trends?.daily === dir &&
      coin.trends?.fourHour === dir
    );
  }, [trackedData, directionFilter]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  const controlsBar = (
    <div className="dashboard-controls">
      <div className="asset-toggle">
        <button
          className={`asset-toggle-btn ${assetMode === 'crypto' ? 'active' : ''}`}
          onClick={() => handleModeChange('crypto')}
        >
          🪙 Crypto
        </button>
      </div>
      <div className="direction-filter">
        <button
          className={`direction-filter-btn ${directionFilter === 'all' ? 'active' : ''}`}
          onClick={() => setDirectionFilter('all')}
        >
          Show All
        </button>
        <button
          className={`direction-filter-btn ${directionFilter === 'long' ? 'active active-long' : ''}`}
          onClick={() => setDirectionFilter('long')}
        >
          ↑ Long Only
        </button>
        <button
          className={`direction-filter-btn ${directionFilter === 'short' ? 'active active-short' : ''}`}
          onClick={() => setDirectionFilter('short')}
        >
          ↓ Short Only
        </button>
      </div>

      <label className="untracked-toggle" title="Coins with no indicator data (not listed on Binance/Coinbase/Kraken/OKX)">
        <input
          type="checkbox"
          checked={showUntracked}
          onChange={e => setShowUntracked(e.target.checked)}
        />
        Show N/A coins
      </label>
    </div>
  );

  if (serverInitialising) {
    return (
      <div className="dashboard-page">
        {controlsBar}
        <div className="dashboard-loading">
          <Loader size="large" text="Server is starting up — pre-computing indicators..." />
          <p style={{ color: 'var(--text-secondary)', marginTop: '12px', fontSize: '0.875rem', textAlign: 'center' }}>
            This takes about 60 seconds on first start. Retrying automatically…
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && marketData.length === 0) {
    return (
      <div className="dashboard-page">
        {controlsBar}
        <div className="dashboard-loading">
          <Loader size="large" text={
            assetMode === 'stocks' ? 'Loading US stocks data...' :
            assetMode === 'tao-subnets' ? 'Loading Tao Subnets data...' :
            'Loading cryptocurrency data...'
          } />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <OnboardingTour run={showOnboarding} steps={DASHBOARD_TOUR_STEPS} onDismiss={dismissOnboarding} />
      {error && (
        <ErrorBanner
          message={error}
          type="error"
          onDismiss={() => setError(null)}
          onRetry={() => fetchMarketData(false, assetMode)}
          className="dashboard-error"
        />
      )}

      {/* Asset mode toggle */}
      {controlsBar}

      <div className="stats-grid">
        <StatCard label="Total Market Cap" value={marketStats.totalMarketCap} type="currency" icon="💰" isLoading={isLoading} />
        <StatCard label="24h Volume"       value={marketStats.totalVolume}    type="currency" icon="📊" isLoading={isLoading} />
        <StatCard label="Top Gainer"       value={topMovers.gainers[0]?.symbol || 'N/A'} change={topMovers.gainers[0]?.price_change_percentage_24h} icon="📈" isLoading={isLoading} />
        <StatCard label="Market Sentiment" value={marketStats.bullishPercent ? `${marketStats.bullishPercent}% Bullish` : 'N/A'} change={marketStats.averageChange} icon="🎯" isLoading={isLoading} />
      </div>

      <SearchBar
        onSearch={t => setSearchTerm(t)}
        placeholder={
          assetMode === 'stocks' ? 'Search stocks...' :
          assetMode === 'tao-subnets' ? 'Search subnets...' :
          'Search cryptocurrencies...'
        }
        disabled={isLoading && marketData.length === 0}
      />

      <MarketTable
        data={directionFilteredData}
        searchTerm={searchTerm}
        isLoading={isLoading && marketData.length === 0}
      />

      {lastUpdated && (
        <div className="last-updated">
          Last updated: {new Date(lastUpdated).toLocaleTimeString()}
          {isLoadingIndicators && <span className="updating-indicator"> • Loading indicators...</span>}
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
